import { Popover, PopoverContent, PopoverTrigger } from '@goaltree/ui/components/popover'
import { cn } from '@goaltree/ui/lib/utils'
import { Clock, X } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { coerceEnd, shiftTime, toMinutes } from '@/lib/time'
import { openTimePicker } from '@/lib/time-input'

const QUICK = [
  { key: 'timeMorning', time: '07:00' },
  { key: 'timeMidday', time: '12:00' },
  { key: 'timeEvening', time: '18:00' },
  { key: 'timeNight', time: '21:00' },
] as const
const DURATIONS = [15, 30, 60, 120] as const
const LAST_DURATION_KEY = 'habitTimePicker.lastDuration'

function readLastDuration(): number {
  try {
    const v = Number(localStorage.getItem(LAST_DURATION_KEY))
    return (DURATIONS as readonly number[]).includes(v) ? v : 30
  } catch {
    return 30
  }
}

function durationLabel(min: number) {
  return min < 60 ? `${min}m` : `${min / 60}h`
}

const stop = (e: { stopPropagation: () => void }) => e.stopPropagation()

// A habit's time of day: a dashed clock when all-day, else one `⏱ 07:00–07:30`
// segment. Either opens the picker; nothing changes until a choice is made.
export function HabitTimePicker({
  allDay,
  start,
  durationMinutes,
  overdue = false,
  onSet,
  onAllDay,
  className,
}: {
  allDay: boolean
  start: string // "HH:MM"
  durationMinutes: number
  overdue?: boolean
  onSet: (start: string, durationMinutes: number) => void
  onAllDay: () => void
  className?: string
}) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [duration, setDuration] = useState(() =>
    allDay ? readLastDuration() : durationMinutes || 30,
  )
  const [customStart, setCustomStart] = useState(start)
  const [customEnd, setCustomEnd] = useState(shiftTime(start, durationMinutes || 30))
  const end = shiftTime(start, durationMinutes || 60)

  const commit = (s: string, d: number) => {
    try {
      localStorage.setItem(LAST_DURATION_KEY, String(d))
    } catch {}
    onSet(s, d)
    setOpen(false)
  }

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (next) {
          setDuration(allDay ? readLastDuration() : durationMinutes || 30)
          setCustomStart(start)
          setCustomEnd(end)
        }
        setOpen(next)
      }}
    >
      <PopoverTrigger
        onClick={stop}
        onMouseDown={stop}
        title={t('inbox.setTime')}
        aria-label={t('inbox.setTime')}
        className={cn(
          allDay
            ? 'flex h-5 w-5 cursor-pointer items-center justify-center rounded-md text-muted-foreground/60 transition-colors hover:bg-muted hover:text-foreground'
            : cn(
                '-mx-1 flex cursor-pointer items-center gap-1 rounded px-1 py-0.5 font-medium tabular-nums transition-colors',
                overdue
                  ? 'text-red-600 hover:bg-red-500/10 dark:text-red-400'
                  : 'text-foreground hover:bg-muted',
              ),
          className,
        )}
      >
        <Clock className={allDay ? 'h-3 w-3' : 'h-2.5 w-2.5'} strokeWidth={2.2} />
        {!allDay && (
          <span>
            {overdue && <span className="font-semibold">{t('inbox.overdue')} · </span>}
            {start}–{end}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent
        className="w-72 gap-0 p-3 font-sans"
        side="bottom"
        align="start"
        onClick={stop}
        onMouseDown={stop}
      >
        <div className="mb-2 flex items-center justify-between">
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
            {t('inbox.timeWhen')}
          </p>
          <button
            type="button"
            aria-label={t('common.close')}
            onClick={() => setOpen(false)}
            className="-my-1 -mr-1 flex size-6 cursor-pointer items-center justify-center rounded-full text-muted-foreground hover:bg-muted hover:text-foreground"
          >
            <X className="size-3.5" />
          </button>
        </div>
        <div className="mb-3 grid grid-cols-4 gap-1.5">
          {QUICK.map((q) => {
            const on = !allDay && start === q.time
            return (
              <button
                key={q.key}
                type="button"
                onClick={() => commit(q.time, duration)}
                className={cn(
                  'cursor-pointer rounded-lg border px-1 py-1.5 text-center text-[11px] font-semibold transition-colors',
                  on
                    ? 'border-primary/50 bg-primary/10 text-primary-text'
                    : 'border-border text-foreground hover:bg-muted',
                )}
              >
                {t(`inbox.${q.key}`)}
                <span
                  className={cn(
                    'mt-0.5 block font-mono text-[10px] font-medium',
                    on ? 'text-primary-text' : 'text-muted-foreground',
                  )}
                >
                  {q.time}
                </span>
              </button>
            )
          })}
        </div>
        <p className="mb-2 text-[10px] font-bold uppercase tracking-widest text-muted-foreground/70">
          {t('inbox.timeFor')}
        </p>
        <div className="mb-3 flex gap-1.5">
          {DURATIONS.map((d) => (
            <button
              key={d}
              type="button"
              onClick={() => {
                setDuration(d)
                setCustomEnd(shiftTime(customStart, d))
                // Already timed: changing the length is a complete choice.
                if (!allDay) commit(start, d)
              }}
              className={cn(
                'flex-1 cursor-pointer rounded-full border py-1 font-mono text-[11px] transition-colors',
                d === duration
                  ? 'border-primary/50 bg-primary/10 font-bold text-primary-text'
                  : 'border-border text-foreground hover:bg-muted',
              )}
            >
              {durationLabel(d)}
            </button>
          ))}
        </div>
        <div className="mb-3 flex items-center gap-2 font-mono text-xs">
          <span className="mr-auto font-sans text-[11px] text-muted-foreground">
            {t('inbox.timeCustom')}
          </span>
          <input
            type="time"
            onClick={openTimePicker}
            lang="en-GB"
            aria-label={t('inbox.startTime')}
            value={customStart}
            onChange={(e) => {
              const s = e.target.value || customStart
              setCustomStart(s)
              setCustomEnd(coerceEnd(s, customEnd))
            }}
            className="rounded-md border border-border bg-background px-1.5 py-1 text-foreground outline-none focus:border-primary/60"
          />
          <span className="text-muted-foreground/60">→</span>
          <input
            type="time"
            onClick={openTimePicker}
            lang="en-GB"
            aria-label={t('inbox.endTime')}
            value={customEnd}
            onChange={(e) => setCustomEnd(coerceEnd(customStart, e.target.value))}
            className="rounded-md border border-border bg-background px-1.5 py-1 text-foreground outline-none focus:border-primary/60"
          />
        </div>
        <button
          type="button"
          onClick={() =>
            commit(customStart, Math.max(15, toMinutes(customEnd) - toMinutes(customStart)))
          }
          className="mb-3 w-full cursor-pointer rounded-lg bg-primary py-1.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90"
        >
          {t('inbox.timeSet')} {customStart}–{customEnd}
        </button>
        <button
          type="button"
          onClick={() => {
            onAllDay()
            setOpen(false)
          }}
          className={cn(
            'flex w-full cursor-pointer items-center justify-center gap-1.5 border-t border-border pt-2.5 text-xs font-medium transition-colors',
            allDay ? 'text-primary-text' : 'text-muted-foreground hover:text-foreground',
          )}
        >
          <Clock className="h-3 w-3" strokeWidth={2.2} />
          {t('inbox.timeAllDay')}
        </button>
      </PopoverContent>
    </Popover>
  )
}
